import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import { addAttachmentFromBase64 } from '../services/office';
import MatterCard from '../components/MatterCard';
import { Folder, FileText, FileImage, FileSpreadsheet, File, FileCode2, Clock, FolderTree } from 'lucide-react';

/**
 * Documents tab — Clio file browser.
 *
 * Two views:
 *  - 'recent' (default in compose) — flat list of the last 25 documents sorted by last modified.
 *    N8N returns { documents: [...] } for this view.
 *  - 'tree'   (default in reading) — full recursive folder/file tree.
 *    N8N returns { tree: [...] } for this view.
 *
 * The active view is toggled via the toolbar. The selected view is sent as
 * params.view in the matterDocuments webhook payload so N8N can route accordingly.
 */
export default function DocumentsTab() {
    const { linkedMatter, userEmail, surface, itemId, conversationId, setStatusLabel } = useAddin();
    const [expandedFolders, setExpandedFolders] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tree, setTree] = useState([]);
    const [recentDocs, setRecentDocs] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const matter = linkedMatter;
    const isComposeMode = surface === 'compose' || surface === 'ComposePane';

    // Compose defaults to recent; reading pane defaults to tree (all documents)
    const [viewMode, setViewMode] = useState(isComposeMode ? 'recent' : 'tree');

    // Re-fetch whenever the matter, user, or view mode changes
    useEffect(() => {
        const matterId = matter?.id || matter?.matter_id;
        if (!matterId) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setSelectedFile(null);

        webhook.matterDocuments({ userEmail, matterId, view: viewMode })
            .then(res => {
                if (!isMounted) return;

                if (viewMode === 'recent') {
                    // Flat list — N8N returns { documents: [...] }
                    setRecentDocs(res.documents || []);
                    setTree([]);
                } else {
                    // Full tree — N8N returns { tree: [...] }
                    const fetchedTree = res.tree || [];
                    setTree(fetchedTree);
                    setRecentDocs([]);

                    // Auto-expand any folder containing "deliverables"
                    const pathsToExpand = {};
                    const searchForDeliverables = (nodes, currentPath) => {
                        for (const node of nodes) {
                            if (node.type === 'folder') {
                                const newPath = [...currentPath, node.id];
                                if (node.name.toLowerCase().includes('deliverables')) {
                                    newPath.forEach(id => pathsToExpand[id] = true);
                                }
                                if (node.children) {
                                    searchForDeliverables(node.children, newPath);
                                }
                            }
                        }
                    };
                    searchForDeliverables(fetchedTree, []);
                    if (Object.keys(pathsToExpand).length > 0) {
                        setExpandedFolders(prev => ({ ...prev, ...pathsToExpand }));
                    }
                }
            })
            .catch(err => {
                console.error('Failed to fetch documents:', err);
                if (isMounted) setError(err.message);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [matter?.id, matter?.matter_id, userEmail, viewMode]);

    const toggleFolder = (folderId) => {
        setExpandedFolders((prev) => ({
            ...prev,
            [folderId]: !prev[folderId],
        }));
    };

    const handleFileAction = async (file, action) => {
        if (action === 'insert' || isComposeMode) {
            try {
                const btn = document.getElementById('attach-to-email-btn');
                if (btn) btn.innerText = 'Attaching...';
                setStatusLabel(`Attaching ${file.name}...`);

                const res = await webhook.attachFromClio({
                    outlookItemId: itemId,
                    conversationId,
                    userEmail,
                    matterId: matter.id || matter.matter_id,
                    matterNumber: matter.display_number || matter.number || matter.matter_number,
                    documentId: file.id
                });

                if (res.status === 'success' && res.base64) {
                    await addAttachmentFromBase64(res.base64, file.name);
                    setStatusLabel('Attached successfully');
                } else {
                    setStatusLabel('Attachment failed');
                    alert('Failed to attach document: ' + (res.error || 'Unknown error'));
                }
            } catch (err) {
                console.error('Failed to attach:', err);
                setStatusLabel('Attachment error');
                alert(`Error attaching file: ${err.message}`);
            } finally {
                const btn = document.getElementById('attach-to-email-btn');
                if (btn) btn.innerText = 'Attach to Email';
            }
        } else {
            window.open(`https://app.clio.com/nc/#/documents/${file.id}/details`, '_blank');
        }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        try {
            const d = new Date(isoString);
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return '';
        }
    };

    /** Returns true if the node or any descendant matches the search query */
    const matchesSearch = (node, query) => {
        if (!query) return true;
        const q = query.toLowerCase();
        if (node.name.toLowerCase().includes(q)) return true;
        if (node.type === 'folder') {
            return (node.children || []).some(child => matchesSearch(child, q));
        }
        return false;
    };

    /** Recursively count files (not folders) under a node */
    const countFiles = (node) => {
        if (node.type === 'file') return 1;
        return (node.children || []).reduce((sum, child) => sum + countFiles(child), 0);
    };

    const getLucideIcon = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        switch (ext) {
            case 'pdf': return <FileCode2 size={16} color="#e53935" />;
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'bmp': return <FileImage size={16} color="#858587" />;
            case 'doc':
            case 'docx':
            case 'txt': return <FileText size={16} color="#1b5e20" />;
            case 'xls':
            case 'xlsx':
            case 'csv': return <FileSpreadsheet size={16} color="#2e7d32" />;
            default: return <File size={16} color="#858587" />;
        }
    };

    const getFileIconWithColor = (filename) => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx'].includes(ext)) {
            return <FileText size={16} color="#1565c0" />;
        }
        return getLucideIcon(filename);
    };

    const ChevronToggle = ({ open, isLeaf }) => (
        <div className={`tree-toggle${open ? ' open' : ''}${isLeaf ? ' leaf' : ''}`}>
            <svg viewBox="0 0 8 8">
                <path d="M2 2l4 2-4 2z" fill="#858587" />
            </svg>
        </div>
    );

    // ── Recent view: flat list ──────────────────────────────────────────────────
    const RecentList = () => {
        const filtered = recentDocs.filter(doc =>
            !searchQuery || doc.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        if (filtered.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>
                    {searchQuery ? 'No documents match your search.' : 'No recent documents found.'}
                </div>
            );
        }

        return (
            <div className="file-tree">
                {filtered.map(doc => (
                    <div
                        key={doc.id}
                        className={`tree-item${selectedFile?.id === doc.id ? ' selected' : ''}`}
                        onClick={() => setSelectedFile(doc)}
                        style={{ paddingLeft: '8px' }}
                    >
                        <ChevronToggle isLeaf />
                        <div className="tree-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {getFileIconWithColor(doc.name)}
                        </div>
                        <div className="tree-name" title={doc.name}>{doc.name}</div>
                        <div className="tree-meta" style={{ whiteSpace: 'nowrap', fontSize: '10px', color: '#8a8a8a' }}>
                            {formatDate(doc.updated_at)}
                        </div>
                        <div className="file-actions">
                            {isComposeMode ? (
                                <button className="file-btn insert" onClick={(e) => { e.stopPropagation(); handleFileAction(doc, 'insert'); }}>Insert</button>
                            ) : (
                                <button className="file-btn view" onClick={(e) => { e.stopPropagation(); handleFileAction(doc, 'view'); }}>Open</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ── Tree view: recursive folder/file nodes ──────────────────────────────────
    const TreeNode = ({ node, depth = 0 }) => {
        if (!matchesSearch(node, searchQuery)) return null;

        if (node.type === 'file') {
            return (
                <div
                    className={`tree-item${selectedFile?.id === node.id ? ' selected' : ''}`}
                    onClick={() => setSelectedFile(node)}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                >
                    <ChevronToggle isLeaf />
                    <div className="tree-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getFileIconWithColor(node.name)}
                    </div>
                    <div className="tree-name" title={node.name}>{node.name}</div>
                    <div className="file-actions">
                        {isComposeMode ? (
                            <button className="file-btn insert" onClick={(e) => { e.stopPropagation(); handleFileAction(node, 'insert'); }}>Insert</button>
                        ) : (
                            <button className="file-btn view" onClick={(e) => { e.stopPropagation(); handleFileAction(node, 'view'); }}>Open</button>
                        )}
                    </div>
                </div>
            );
        }

        // Folder node
        const isExpanded = !!expandedFolders[node.id];
        const fileCount = countFiles(node);

        return (
            <div className="tree-node">
                <div
                    className="tree-item folder-root"
                    onClick={() => toggleFolder(node.id)}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                >
                    <ChevronToggle open={isExpanded} />
                    <div className="tree-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Folder size={16} color="#50AEB3" fill={isExpanded ? "#e0f2f1" : "none"} />
                    </div>
                    <div className="tree-name">{node.name}</div>
                    <div className="tree-meta">{fileCount} {fileCount === 1 ? 'file' : 'files'}</div>
                    <div className="file-actions">
                        <button className="file-btn view" onClick={(e) => {
                            e.stopPropagation();
                            const mId = matter.id || matter.matter_id;
                            window.open(`https://app.clio.com/nc/#/matters/${mId}/documents?folderId=${node.id}`, '_blank');
                        }}>Open</button>
                    </div>
                </div>
                {isExpanded && (
                    <div className="tree-children">
                        {(node.children || []).map(child => (
                            <TreeNode key={child.id} node={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (!matter) {
        return (
            <div className="addin-body" style={{ padding: '20px', textAlign: 'center', color: '#8a8a8a' }}>
                <p>No matter linked.</p>
            </div>
        );
    }

    return (
        <div className="addin-body">
            <MatterCard
                label="Documents for"
                name={matter.description || matter.name || matter.matter_description}
                number={matter.display_number || matter.number || matter.matter_number}
                status={matter.status}
            />

            {/* Search + view toggle toolbar */}
            <div className="doc-toolbar">
                <div className="doc-search">
                    <svg viewBox="0 0 12 12">
                        <circle cx="5" cy="5" r="3.5" fill="none" stroke="#858587" strokeWidth="1.3" />
                        <path d="M8 8l2 2" stroke="#858587" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search documents..."
                        id="doc-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* View mode toggle — icon only */}
                <div className="doc-view-toggle" style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                    <button
                        className={`doc-view-btn${viewMode === 'recent' ? ' active' : ''}`}
                        onClick={() => setViewMode('recent')}
                        title="Recent — last 25 documents sorted by modified date"
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '26px', height: '26px',
                            borderRadius: '4px 0 0 4px',
                            border: '1px solid var(--border, #e0e0e0)',
                            background: viewMode === 'recent' ? 'var(--teal, #50AEB3)' : 'transparent',
                            color: viewMode === 'recent' ? '#fff' : 'var(--text-muted, #858587)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            padding: 0,
                        }}
                    >
                        <Clock size={13} />
                    </button>
                    <button
                        className={`doc-view-btn${viewMode === 'tree' ? ' active' : ''}`}
                        onClick={() => setViewMode('tree')}
                        title="Folders — all documents in folder structure"
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '26px', height: '26px',
                            borderRadius: '0 4px 4px 0',
                            border: '1px solid var(--border, #e0e0e0)',
                            borderLeft: 'none',
                            background: viewMode === 'tree' ? 'var(--teal, #50AEB3)' : 'transparent',
                            color: viewMode === 'tree' ? '#fff' : 'var(--text-muted, #858587)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            padding: 0,
                        }}
                    >
                        <FolderTree size={13} />
                    </button>
                </div>
            </div>

            {/* Breadcrumbs */}
            <div className="folder-path-bar">
                <span className="fp-crumb">{viewMode === 'recent' ? 'Recent' : 'All'}</span>
                <span className="fp-sep">›</span>
                <span className="fp-crumb active">Matters / {matter.display_number || matter.number || matter.matter_number}</span>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>Loading documents...</div>
            ) : error ? (
                <div style={{ color: 'var(--orange)', padding: '10px' }}>Error: {error}</div>
            ) : viewMode === 'recent' ? (
                <RecentList />
            ) : (
                <div className="file-tree">
                    {tree.map(node => (
                        <TreeNode key={node.id} node={node} depth={0} />
                    ))}
                    {tree.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>No documents found.</div>
                    )}
                </div>
            )}

            {/* Selected file action bar */}
            {selectedFile && (
                <>
                    <div className="selected-files-bar">
                        <div className="sf-icon">
                            <svg viewBox="0 0 18 18" fill="none">
                                <rect x="2" y="1" width="11" height="14" rx="1.5" fill="none" stroke="#50AEB3" strokeWidth="1.4" />
                                <path d="M5 6h7M5 8.5h7M5 11h4" stroke="#50AEB3" strokeWidth="1.2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <div className="sf-text">
                            <div className="sf-name">{selectedFile.name}</div>
                            <div className="sf-size">
                                {formatSize(selectedFile.size)}
                                {selectedFile.updated_at && ` · ${formatDate(selectedFile.updated_at)}`}
                                {' · '}Selected for {isComposeMode ? 'attachment' : 'opening'}
                            </div>
                        </div>
                        <div className="sf-remove" onClick={() => setSelectedFile(null)}>✕</div>
                    </div>
                    <button className="confirm-btn" id="attach-to-email-btn" onClick={() => handleFileAction(selectedFile, isComposeMode ? 'insert' : 'open')}>
                        {isComposeMode ? 'Attach to Email' : 'Open Document'}
                    </button>
                </>
            )}
        </div>
    );
}
